import {
  ICreateStakeholderpayload,
  UploadStaStakeHolders,
} from '@efex-services/client/types';
import { IOnboardingDocuments } from '@efex-services/documents/documentsSvc';
import { DateTime } from 'luxon';
import { useState } from 'react';
import STAKEHOLDER_OPTIONS, {
  StakeHolderId,
} from '@pages/onboarding/Mexico/constants/stakeHolderOptions';

export type StakeholderCountry = 'USA' | 'MX';

const useNewStakeholderService = (country: StakeholderCountry = 'MX') => {
  const [metadata, setMetadata] = useState<Record<
    string,
    string | undefined
  > | null>(null);

  const generateStakeholderPayload = (
    stakeholder: UploadStaStakeHolders,
    documents: IOnboardingDocuments[]
  ): ICreateStakeholderpayload => {
    const documentIds = documents.map((doc) => doc.id);
    const {
      addressLine1,
      addressLine2,
      birthDate,
      entityTypeId,
      countryBirthId,
      city,
      curp,
      businessSectorId,
      effectiveAt,
      expiredAt,
      federalEntityId,
      gender,
      idNumber,
      lastName,
      name,
      nationalityCountryId,
      neighborhood,
      percentage,
      postCode,
      postalCodeId,
      secondLastName,
      state,
      title,
      legalRepresentativeStatament,
      legalRepresentativeResponse,
      resourceFamilyStatament,
      resourceFamilyResponse,
      extNumber,
      intNumber,
      ssn,
    } = stakeholder;

    setMetadata({
      effectiveAt,
      expiredAt,
      number: idNumber,
    });

    const address = {
      addressLine1,
      addressLine2,
      city,
      neighborhood,
      postCode,
      postalCodeId,
      state,
      extNumber,
      intNumber,
    };

    const getEntityTypeId = (value: string | StakeHolderId | undefined) => {
      if (!value) return STAKEHOLDER_OPTIONS[0].id;
      if (typeof value === 'string') {
        return STAKEHOLDER_OPTIONS.find((option) => option.value === value)?.id;
      }
      return value;
    };

    const basePayload = {
      birthDate: DateTime.fromISO(birthDate || '').toFormat('yyyy-MM-dd'),
      countryBirthId,
      lastName,
      name,
      nationalityCountryId,
      percentage,
      secondLastName,
      title,
      documentIds,
      address,
    };

    if (country === 'USA') {
      const usaPayload = {
        ...basePayload,
        gender: gender === 'Female' ? ('F' as const) : ('M' as const),
        ssn,
      };

      return usaPayload;
    }

    const mexicoPayload = {
      ...basePayload,
      curp,
      federalEntityId,
      gender: (() => {
        const entityTypeValue = getEntityTypeId(entityTypeId);
        if (
          entityTypeValue === StakeHolderId.JURIDICAL_MX ||
          entityTypeValue === StakeHolderId.JURIDICAL_FR
        ) {
          return undefined;
        }
        return gender === 'Female' ? ('F' as const) : ('M' as const);
      })(),
      businessSectorId: businessSectorId ? Number(businessSectorId) : 1,
      legalRepresentativeStatament: legalRepresentativeStatament ?? false,
      legalRepresentativeResponse: legalRepresentativeStatament
        ? legalRepresentativeResponse || ''
        : '',
      resourceFamilyStatament: resourceFamilyStatament ?? false,
      resourceFamilyResponse: resourceFamilyStatament
        ? resourceFamilyResponse || ''
        : '',
      entityTypeId: entityTypeId
        ? getEntityTypeId(entityTypeId)
        : STAKEHOLDER_OPTIONS[0].id,
      ssn,
    };

    if (mexicoPayload.gender === undefined) {
      delete mexicoPayload.gender;
    }

    delete mexicoPayload.address.addressLine2;
    delete mexicoPayload.address.neighborhood;

    return mexicoPayload;
  };

  return { metadata, generateStakeholderPayload };
};

export default useNewStakeholderService;
